import paceCardInputTemplate from './paceCardInput.template.html';

/**
 * @ngdoc component
 * @module csm.payments.pace
 * @name   csm.payments.pace.component:paceCardInput
 *
 * @param {Expression} fuzeAccountId Expression that should evaluate to one of the following:
 *                                     - string: A Pace fuze accountID
 *                                     - array:  A list of Pace fuze account IDs
 *                                     - object: An object whose values are Pace fuze account IDs
 * @param {Expression} publicKey     A Pace public api key when evaluated
 * @param {Expression} postUrl       String Url to post card information when evaluated
 * @param {Expression} tokenCreated  Expression that will be called when a
 *                                   token is successfully created. The expression
 *                                   will have the json results from Pace available
 *                                   as $resp. $resp data type will depend on fuzeAccountId's value:
 *                                     - string: The response from the CreateCCToken request from PACE
 *                                     - array:  An array of CreateCCToken responses corresponding to order
 *                                               of supplied fuzeAccountIDs
 *                                     - object  An object with keys matching from fuzeAccountId with the
 *                                               value being the corresponding CreateCCToken response
 *
 *                                   If a promise is returned as a result of the expression, the rejected
 *                                   promise's message will be displayed in the form.
 * @description
 * Generates a form that is used to take payment card information and send the
 * details to pace which upon valid input will generate a token to be used
 * afterwards with processing.
 *******************************************************************************/
/* @ngInject */
export function paceCardInputComponent($http, $q, BusyScreen) {
  /* Pace Result Codes */
  var RC_SUCCESS = 0;
  var RC_DECLINED = 1;
  var RC_ERROR = 2;

  /*-------------------*/
  /* Definition Object */
  /*-------------------*/
  if (!(this instanceof paceCardInputComponent))
    return {
      bindings: {
        fuzeAccountId: '&',
        publicKey: '&',
        postUrl: '&',
        tokenCreated: '&'
      },
      template: paceCardInputTemplate,
      controller: paceCardInputComponent
    };

  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Self Reference. */ var $ctrl = this;
  /** Validators Object. */ $ctrl.validators = {};
  /** Request Payload. */ $ctrl.request = {};
  /** Prevent multiple submitions */ $ctrl.disableSubmit = false;

  /** Card Types Definition Source. */
  $ctrl.cardTypes = [
    {
      type: 'American Express',
      paceType: 1,
      testExp: /^3/,
      image:
        'data:image/jpeg;base64,/9j/4AAQSkZJRgABAgAAZABkAAD/2wBDABYTEx4VHi8cHC87LSUtOzcuLCwuN0U/Pz8/P0VFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUX/2wBDARceHiYhJi4dHS5ALiYuQEVAMzNARUVFRT9FRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUX/wAARCAAmADwDASIAAhEBAxEB/8QAGQAAAwEBAQAAAAAAAAAAAAAAAAQFAQMG/8QAQhAAAQIDAwUHEQkAAAAAAAAAAQIRAAMEEiExBRMUQWEVIiNDUZHTMjNEUlNicXKBkpOxssLR0uEkJUVUY4KhwcP/xAAYAQEBAQEBAAAAAAAAAAAAAAAABAMBAv/EAB0RAAMBAAIDAQAAAAAAAAAAAAABAhEDURIhoTH/2gAMAwEAAhEDEQA/AHaGipplPKKpaLRQgk2AX3o2PDu51NrkyxtsJb1RmTg9LJv4tHsiHhsbyH+jdAHn9Myd+XHo0QGrydqpxr4tEIDJ883gDz06sdep7+SM0CdyJ89HzRT4R39JPPk6+Dxq8n9wGzg0QzSy6GqexKSAnF0Jc+uJOgTnZkue/R80UcioMuZMSq5SbjrvePNzKWye4qm8oppybSqvVJlgeIn4fWJ+iU26VjNozeYtWbAZ7eOHJc8XW1tzxKc7q49j/wCkYFA1k0HRJJuHBox8UbYaJfb+2Fsmj7LJ3vFo9kRlTWIkEW3IL4YR3NObhDp6vPKsqQhIZ3ZWzvwORy/hh0qCbsWHaKuF/wCtgb9h2iPPomKlm0glJ5QWjrpk88Yu7vjFbnojXJi9/paJSpQSQCSGvRe14brz82Bvxjrk9ATOmKJe2Lfa4qVgxU/PEHTajui/PPxitkacuYtapiiq5IdV/LGVy0jWKTZcV4Dz/WI/4ph2P78VFlg7c0Rn+8315j34wKBmiqJopZQzKyBLReDLv3ov6t28gjKmpDDPSF2HutGXj6SCCAFlVNO5tSd8998rpINJpO4nVrldJBBAG6TSN1g88rpIYpqlN+jyVPrsmV/PCQQQB10mYTvZMwHYZfSfSJufXujazRtZlrLo7fHq7OzHyQQQB//Z'
    },
    {
      type: 'Discover',
      paceType: 2,
      testExp: /^6/,
      image:
        'data:image/jpeg;base64,/9j/4AAQSkZJRgABAgAAZABkAAD/2wBDABoZGSccJz4lJT5CLy8vQkc9Ozs9R0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0f/2wBDARwnJzMmMz0mJj1HPTI9R0dHRERHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0f/wAARCAAqAEMDASIAAhEBAxEB/8QAGgAAAgMBAQAAAAAAAAAAAAAAAAMBAgUEBv/EADcQAAEDAwEEBQkJAQAAAAAAAAEAAhEDEiEEMTJRYRNBgZHwBSIzQlJicbHRFBU0goOSk6LB4f/EABgBAAMBAQAAAAAAAAAAAAAAAAABAgME/8QAIREBAAEEAQQDAAAAAAAAAAAAAAERIUFhAhIiMVGBoeH/2gAMAwEAAhEDEQA/AOJ1VuoDq2olxugAHdHAclQadguOXNsuYqUrHUyxzg03TlNbWp5YDDQ20Hispmb+XTEcaRWn6gaamQ0Zki5x5cFDWUK5sphwPtHgpdXpteGzc221xUMFPTm9tQO6oAzH/FN91wO3VMuyp5Op2YBaeP1XI7Ts6VkDzCJP+rvq61oZ6QOj1Qs5mob0RJMPEwPip49dL1VPRr4WLKQZcGE3Tmd3grOpUhIDSCwTdOCltex1MDpLC0bucqalZlS5hdggQc7VV9/Y7aY8a9GfeVezeO5Eyfb+O2MXbYQs64Wx7sf2lC3cobTc8mOoSru0zmmMZ58Z+iKRcH+aYPjinvNRglztmzA6pH1SDmpUXVcNjHHmmnSP2y3v7u/qSW1HUybTEqx1FQ4kp3Ap0HVBIIGYyVd2lc0EkjHPt+WUllV7BDTAUms8iCTGxFwfT0bqjQ4EZVq2jNJhfcDCdp6sNAkd6dqHD7O6XAkkYkFK5sZCEKiO6MhxafNImezq/wAUtp3QC6AROfktPy3+I9TYNm9+bnw92FkJA6pQawEhwPLwVzK3chEBAQrBS3sTDaoCabC572i3dYCe0dW1U1L7qDgBUGASXz7Qx8lns3R6Ts2dio/aN7b6+zxx5KMoyXY+LoxF3ZNs/ux8UL2X8HoPH6CFa3//2Q=='
    },
    {
      type: 'MasterCard',
      paceType: 3,
      testExp: /^5/,
      image:
        'data:image/jpeg;base64,/9j/4AAQSkZJRgABAgAAZABkAAD/2wBDABoZGSccJz4lJT5CLy8vQkc9Ozs9R0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0f/2wBDARwnJzMmMz0mJj1HPTI9R0dHRERHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0dHR0f/wAARCAApAEEDASIAAhEBAxEB/8QAGgAAAwADAQAAAAAAAAAAAAAAAAQFAgMGAf/EADIQAAEDAgIHBAsBAAAAAAAAAAEAAhEDEiExBAUTIkFRYXGhwfAUFTJCUmKBkbHR4VP/xAAXAQEBAQEAAAAAAAAAAAAAAAAEAwIF/8QALxEAAQMBBQUGBwAAAAAAAAAAAQACEQMSEyExgQRRcZHhFCMyQqHwIkFhgrHB0f/aAAwDAQACEQMRAD8A5tbm0ScTgvaDLjzTVpzjJUq1S02WpdGiHC2/QLRsWrB1GMkwt1FlxkhxHyhGvnNxlLdRYRlyWGj6tdUFzzaOXH+J31XR5u+/8TFB3uw/temYIRX7RVJ8UcEe6a3CFEr6rLRNM3dDmpZBBg5rr1I1no2G2aOjvA+CTs+0lzrFTGcio1KYAtNUZCELqoqsao3Xmp/m17u6PFX305a6m2MakukTPvnAZ4WiFx9B9pI5+YTYqOBkOMzOZz59qBUNlxBTWUzUAcDlhyV/YsLwBTF7mCCWGwGT7TZ3THVZBgAALcadNpENLpLjjugi4N7pXP7Z/wATsfmK20Krg4DFxybvER0H6Ui8bvwq3L48SsTTaW4EbSrG9IgQJwJ4u/KYc2qXOJmBjaW3dBaJH5U1lO477I7XXZ5rfYOuGGZy5Z5dEc1WzkdIg8VM0zv9/TRPFrQ8Ngbz7fsyTH1wSGsgRorrmhpc6cOhELKwRH1U3WVW1mzBzO8J88R5lbpvD3hrBGI3ZSD/AHmsOaWiSVDQhC7KIhMNrfEl0KNSxHx9Vejbnu9dyc2reawdWHBLIRW3U4zr0TX30YWdM/VVtG1kGNDKgy4jxCe9PoRN3cf0ubQi1ez2vN9sR6/pQbeRjGqtV9aNAikJPMqO5xeS5xklYoTtnuo7rP5zn74YKFS15+iEIQmKK//Z'
    },
    {
      type: 'Visa',
      paceType: 4,
      testExp: /^4/,
      image:
        'data:image/gif;base64,R0lGODlhQwAqALMAAP///+7x9d3k6//Uq8HO3//AYP+wOqO1xf+ZAIObt2WAp0RjjChPeBZJcwAzZgAoXiH5BAQUAP8ALAAAAABDACoAAAT/0MlJq7046y2B/2AojmRpmt2prizbEcwiz3Rt33iu04rXHY+gcEgsGo/IJHHhcwCAnKh0WmECfg+qdouxYrlgsNcJDZun46dyzW4n0wdHY06v2+/4vH5fTwMCgIGCg4SFhoeIhE0tjI2NKSMEAoIikwECHpMCmB4BBJ8EASSWgCqQIgcKDKsMByAHDQwNCU+xDa4ACbISsQmcH6qsPSenIgELQrSdDEENmMhBBLlHVh4EDkPVJcUi19jD09i0AgzYTAINvAqqDw3SwFkTDKZOJgITw94ODKJQDz0JsjzAJUABLmvqsjSgpyKdAysKBOKKKEEaxX+iRkBbUOYXCW4i/8o9BODtQTWHC9VIMPnulcSOxOqZWCBhHrKKCL99uDlB2c4g88q0/CizBMVbAsEFxHYwgAJsK8GVcTU1poql++L9guYsxAGROAOI5Ecynk+iV+Ot9InOXCcQx9aGkyMD7NkRIEMQULsv41xcC3ztXHkggEMiUa2e0McUBE0JmAK2W6Dg8YN5WGOxWqkNb9FR2IJoCxDaCjMjDiQ1aBYK0DXRik0cW3ULBIxVygioohBLWgJbZ2fH8Ot5hSWPfyyFEEDggPNQmTQRB3A8tqPr2EN0GFCgu/fv4MOLH0++fPhFAxCoX8++vfv38OPLb28A/fz7+PPLr3/FSXr9AAY4Hzt/2wlo4IHrEegfggwGqCAA/zUo4YD2TWghfA9GeOGG6j0YwAAghijiiCSWaOKJKJK4SHYsZnfGi2FEAAA7'
    }
  ];

  /** Months dataprovider. */
  $ctrl.months = [
    { display: 'January', paceValue: 1 },
    { display: 'February', paceValue: 2 },
    { display: 'March', paceValue: 3 },
    { display: 'April', paceValue: 4 },
    { display: 'May', paceValue: 5 },
    { display: 'June', paceValue: 6 },
    { display: 'July', paceValue: 7 },
    { display: 'August', paceValue: 8 },
    { display: 'September', paceValue: 9 },
    { display: 'October', paceValue: 10 },
    { display: 'November', paceValue: 11 },
    { display: 'December', paceValue: 12 }
  ];

  /*----------------*/
  /* Initialization */
  /*----------------*/
  /** Set the years dataprovider to be nine years from the current year. */
  var today = new Date();
  ($ctrl.years = Array.apply(null, { length: 20 })).reduce(function (
    currentYear,
    e,
    i,
    a
  ) {
    a[i] = { year: currentYear + i, paceValue: (currentYear + i) % 2000 };
    return currentYear;
  },
  today.getFullYear());

  /*----------------------------------------------*/
  /* Default Expiration to current month and year */
  /*----------------------------------------------*/
  $ctrl.request.ExpirationMonth = today.getMonth() + 1;
  $ctrl.request.ExpirationYear = $ctrl.years[0].paceValue;

  /*****************************************************************************
   * Methods
   *****************************************************************************/
  $ctrl.cardNumber = cardNumber;
  $ctrl.$onInit = $onInit;
  $ctrl.submit = submit;

  /*****************************************************************************
   * cardNumber */
  /**
   * Getter/setter for the CardNumber property on the request object. This will
   * set the CardType property based on the testing expression from the cardTypes
   * list.
   *
   * @param value Card number string to set
   *****************************************************************************/
  function cardNumber(value) {
    if (!arguments.length) return $ctrl.request.CardNumber;

    $ctrl.request.CardNumber = value;
    $ctrl.currentCard = $ctrl.cardTypes.find(function (ct) {
      return ct.testExp.test(value);
    });
    $ctrl.request.CardType = ($ctrl.currentCard || {}).paceType;
  }

  /*****************************************************************************
   * createToken */
  /**
   * Attempts to generate a token from the entered card information and the
   * provided fuzeAccountID by posting the data to the provided postUrl value.
   *
   * @param   fuzeAccountID  Fuze Account ID to reference when performing token
   *                         creation
   * @returns                Promise that resolves with PACE CreateCCToken response
   *                         object or rejects with an error message.
   *****************************************************************************/
  function createToken(fuzeAccountID) {
    return $http
      .post(
        $ctrl.postUrl(),
        angular.extend({}, $ctrl.request, { Fuze_Account_ID: fuzeAccountID })
      )
      .then(
        /*-----------------------------------------------*/
        /* Successful response from PACE, verify results */
        /*-----------------------------------------------*/
        function (resp) {
          var resultCode = resp.data.ResultCode;

          /*----------------------------*/
          /* Token created successfully */
          /*----------------------------*/
          if (resultCode == RC_SUCCESS) return resp.data;
          /*------------------------*/
          /* Problem creating token */
          /*------------------------*/ else
            return $q.reject(
              resultCode == RC_DECLINED
                ? 'Please verify your card information, or use another card and try again.'
                : 'Please verity your card information and try again.'
            );
        },
        /*---------------------*/
        /* Communication Error */
        /*---------------------*/
        function (resp) {
          return $q.reject(
            'We are having trouble communicating with the server. Please try again later'
          );
        }
      );
  }

  /*****************************************************************************
   * $onInit */
  /**
   * Called when the component has been initialized and sets the bindings on the
   * request object.
   *****************************************************************************/
  function $onInit() {
    $ctrl.request.Fuze_PUBLIC_API_Key = $ctrl.publicKey();
  }

  /*****************************************************************************
   * submit */
  /**
   * Attempt to generate a token by posting the input data to the specified
   * postUrl. When successful, the tokenCreated expression is evaluated with the
   * response data. Otherwise, rejected promise errors will be displayed in the UI.
   *****************************************************************************/
  function submit() {
    $ctrl.errorMessage = '';

    if (!$ctrl.validators.validate()) return;

    var fuze = $ctrl.fuzeAccountId();

    $ctrl.disableSubmit = true;

    BusyScreen.promise(
      /*-------------------------------------------------------*/
      /* Get token from card data. Generate promises so the    */
      /* result will correspond to the fuzeAccountID data type */
      /*-------------------------------------------------------*/
      (angular.isString(fuze)
        ? createToken(fuze)
        : $q.all(
            angular.isArray(fuze)
              ? fuze.map(createToken)
              : Object.keys(fuze).reduce(function (o, prop) {
                  return (o[prop] = createToken(fuze[prop])), o;
                }, {})
          )
      )

        /*-------------------------------------------------*/
        /* Tokens Succesfully Created - Execute expression */
        /*-------------------------------------------------*/
        .then(function (response) {
          return $ctrl.tokenCreated({ $resp: response });
        })

        /*-----------------------------------------*/
        /* Error Message handling for reject cases */
        /*-----------------------------------------*/
        .then(angular.noop, function (error) {
          $ctrl.errorMessage = error;
        })

        /*---------------------------------------------*/
        /* Reset button to be enabled after processing.*/
        /*---------------------------------------------*/
        .finally(function () {
          $ctrl.disableSubmit = false;
        })
    );
  }
}
